import { useQuery,useQueryClient } from "@tanstack/react-query";
import React from "react";
import { DEFAULT_MANDATE_REFERENCE_PATTERN } from "../../../app/utils/mandateReference";
import { accountName } from "../../../app/utils/accountName";
import type { ApiClient } from "../../../app/api/client";
import type { ContractOptionGroupDto } from "../../../app/utils/contractOptionGroups";
import type {
  CancellationModelLite,
  ContractTemplateBlockDto,
  ContractTemplateDto,
  PaymentMethodDefinitionDto,
  PaymentPlanDto,
  WalletProductDto
} from "./personDetailTypes";
import { PERSON_DETAIL_LOOKUP_STALE_TIME_MS,personDetailQueryKeys } from "./personDetailQueryKeys";

export type PersonDetailAccountLookup = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  name: string;
  email?: string | null;
  isActive: boolean;
};

export type PersonDetailPriceAdjustmentLookup = {
  id: string;
  name: string;
  adjustmentType: number;
  adjustmentValue: number;
  cycleUnit: number;
  isActive: boolean;
};

export type PersonDetailActiveLookup = {
  id: string;
  name: string;
  isActive: boolean;
};

type UsePersonDetailLookupsArgs = {
  api: Pick<ApiClient, "get">;
  canLoadPaymentMethodDefs: boolean;
  canLoadSepaGroups: boolean;
  canLoadRestTemplates: boolean;
  canLoadPaymentPlans: boolean;
  canLoadContractTemplateBlocks: boolean;
  canLoadCancellationModels: boolean;
  canLoadPriceAdjustments: boolean;
};

export function usePersonDetailLookups({
  api,
  canLoadPaymentMethodDefs,
  canLoadSepaGroups,
  canLoadRestTemplates,
  canLoadPaymentPlans,
  canLoadContractTemplateBlocks,
  canLoadCancellationModels,
  canLoadPriceAdjustments
}: UsePersonDetailLookupsArgs) {
  const queryClient = useQueryClient();

  const loadContractTemplateBlocks = React.useCallback(async () => {
    const list = (await api.get<ContractTemplateBlockDto[]>("/api/v1/contract-templates")) || [];
    return list.filter((x) => x.isActive !== false);
  }, [api]);

  const loadPaymentPlans = React.useCallback(async () => {
    const list = (await api.get<PaymentPlanDto[]>("/api/v1/payment-plans")) || [];
    return list.filter((p) => p.isActive);
  }, [api]);

  const loadCancellationModels = React.useCallback(async () => {
    const list = (await api.get<CancellationModelLite[]>("/api/v1/cancellation-models")) || [];
    return list.filter((m) => m.isActive !== false);
  }, [api]);

  const loadPriceAdjustments = React.useCallback(async () => {
    const list = (await api.get<PersonDetailPriceAdjustmentLookup[]>("/api/v1/price-adjustments")) || [];
    return list.filter((d) => d.isActive);
  }, [api]);

  const loadAccounts = React.useCallback(async () => {
    const list = (await api.get<PersonDetailAccountLookup[]>("/api/v1/users")) || [];
    return list
      .filter((e) => e.isActive !== false)
      .sort((a, b) => {
        const al = accountName(a);
        const bl = accountName(b);
        return al.localeCompare(bl, "de");
      });
  }, [api]);

  const loadPaymentMethodDefinitions = React.useCallback(async () => {
    const list = (await api.get<PaymentMethodDefinitionDto[]>("/api/v1/payment-method-definitions")) || [];
    return list;
  }, [api]);

  const loadSepaGroups = React.useCallback(async () => {
    const list = (await api.get<PersonDetailActiveLookup[]>("/api/v1/sepa-groups")) || [];
    return list.filter((g) => g.isActive);
  }, [api]);

  const loadRestTemplates = React.useCallback(async () => {
    const list = (await api.get<PersonDetailActiveLookup[]>("/api/v1/rest-period-templates")) || [];
    return list;
  }, [api]);

  const loadContractTemplates = React.useCallback(async () => {
    const list = (await api.get<ContractTemplateDto[]>("/api/v1/contract-package-templates")) || [];
    return list.filter((t) => t.isActive);
  }, [api]);

  const loadContractOptionGroups = React.useCallback(async () => {
    const list = (await api.get<ContractOptionGroupDto[]>("/api/v1/contract-option-groups")) || [];
    return list.filter((g) => g.isActive !== false);
  }, [api]);

  const loadWalletProducts = React.useCallback(async () => {
    const list = (await api.get<WalletProductDto[]>("/api/v1/wallet-products")) || [];
    return list.filter((w) => w.id);
  }, [api]);

  const mandatePatternQuery = useQuery({
    queryKey: ["lookups", "mandate-reference-settings"],
    queryFn: async () => {
      const value = await api.get<{ pattern?: string | null }>("/api/v1/settings/mandate-reference-settings");
      return String(value?.pattern || "").trim() || DEFAULT_MANDATE_REFERENCE_PATTERN;
    },
    enabled: false,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const contractTemplatesQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.contractTemplates,
    queryFn: loadContractTemplates,
    enabled: canLoadContractTemplateBlocks,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const walletProductsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.walletProducts,
    queryFn: loadWalletProducts,
    enabled: canLoadContractTemplateBlocks,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const contractTemplateBlocksQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.contractTemplateBlocks,
    queryFn: loadContractTemplateBlocks,
    enabled: canLoadContractTemplateBlocks,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const contractOptionGroupsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.contractOptionGroups,
    queryFn: loadContractOptionGroups,
    enabled: canLoadContractTemplateBlocks,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const paymentPlansQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.paymentPlans,
    queryFn: loadPaymentPlans,
    enabled: canLoadPaymentPlans,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const cancellationModelsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.cancellationModels,
    queryFn: loadCancellationModels,
    enabled: canLoadCancellationModels,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const priceAdjustmentsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.priceAdjustments,
    queryFn: loadPriceAdjustments,
    enabled: canLoadPriceAdjustments,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const accountsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.accounts,
    queryFn: loadAccounts,
    enabled: false,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const paymentMethodDefsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.paymentMethods,
    queryFn: loadPaymentMethodDefinitions,
    enabled: canLoadPaymentMethodDefs,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const sepaGroupsQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.sepaGroups,
    queryFn: loadSepaGroups,
    enabled: canLoadSepaGroups,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });
  const restTemplatesQuery = useQuery({
    queryKey: personDetailQueryKeys.lookups.restTemplates,
    queryFn: loadRestTemplates,
    enabled: canLoadRestTemplates,
    staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
  });

  const loadContractTemplatesFromQuery = React.useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.lookups.contractTemplates,
        queryFn: loadContractTemplates,
        staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
      }),
    [loadContractTemplates, queryClient]
  );
  const loadAccountsFromQuery = React.useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: personDetailQueryKeys.lookups.accounts,
        queryFn: loadAccounts,
        staleTime: PERSON_DETAIL_LOOKUP_STALE_TIME_MS
      }),
    [loadAccounts, queryClient]
  );
  const setContractTemplateBlocks = React.useCallback(
    (value: React.SetStateAction<ContractTemplateBlockDto[]>) => {
      queryClient.setQueryData<ContractTemplateBlockDto[]>(
        personDetailQueryKeys.lookups.contractTemplateBlocks,
        (old) => (typeof value === "function" ? value(old || []) : value)
      );
    },
    [queryClient]
  );

  return {
    contractTemplates: contractTemplatesQuery.data || [],
    walletProducts: walletProductsQuery.data || [],
    contractTemplateBlocks: contractTemplateBlocksQuery.data || [],
    contractOptionGroups: contractOptionGroupsQuery.data || [],
    setContractTemplateBlocks,
    paymentPlans: paymentPlansQuery.data || [],
    cancellationModels: cancellationModelsQuery.data || [],
    priceAdjustments: priceAdjustmentsQuery.data || [],
    mandatePattern: mandatePatternQuery.data || DEFAULT_MANDATE_REFERENCE_PATTERN,
    accounts: accountsQuery.data || [],
    paymentMethodDefs: paymentMethodDefsQuery.data || [],
    sepaGroups: sepaGroupsQuery.data || [],
    restTemplates: restTemplatesQuery.data || [],
    loadContractTemplates: loadContractTemplatesFromQuery,
    loadAccounts: loadAccountsFromQuery
  };
}
